import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import BiomarkerGeneSelect, { toBiomarkerGeneOptions } from '../ReusableComponent/BiomarkerGeneSelect';
import DataTable from 'react-data-table-component';
import Loader from 'react-loaders';
import { AiOutlineDownload, AiOutlineSearch } from 'react-icons/ai';
import { FiRotateCcw, FiChevronDown } from 'react-icons/fi';
import axiosInstance from '../configuration/Request';
import backlogo from '../../assets/images/svg/Back.svg';
import GeneDetailPanel from '../ReusableComponent/GeneDetailPanel';
import PrognosisGeneDetailPanel from '../ReusableComponent/PrognosisGeneDetailPanel';
import PrognosisResultsTable from '../ReusableComponent/PrognosisResultsTable';
import { getPrognosisTableGroups } from '../ReusableComponent/prognosisTableModel';
import RoundedPagination from '../ReusableComponent/RoundedPagination';
import { roundedSelectStyles, dataTableCustomStyles, formatStat } from '../ReusableComponent/biomarkerTableStyles';

const GENE_OPTIONS = [{ value: 'ACVR1B', label: 'ACVR1B' }];
const PAGE_SIZE = 10;
const GENE_COL_WIDTH = '170px';

const FILTER_TAB_MAP = {
  cohorts: 'cohorts',
  prognotic: 'prognotic',
  treatment: 'treatment',
};

class BioMarkerGeneResultPage extends Component {
  constructor(props) {
    super(props);
    const searchParams = new URLSearchParams(props.location?.search || '');
    const routeState = props.location?.state || {};
    const queryExpressionType = searchParams.get('expressionType');
    const initialExpressionType = queryExpressionType
      ? queryExpressionType.split(',').map((value) => value.trim()).filter(Boolean)
      : routeState.expressionType || null;
    const geneName = searchParams.get('geneName') || routeState.geneName || 'ACVR1B';
    const cancerTypeLabel = searchParams.get('cancerTypeLabel') || routeState.cancerTypeLabel || 'Non-Small Cell Lung Cancer';
    const defaultMolecularTypes = Array.isArray(initialExpressionType)
      ? initialExpressionType.map((value) => String(value).trim())
      : [String(initialExpressionType || 'cnv').trim()];

    this.state = {
      currentView: 'cohorts',
      selectedGene: geneName,
      initialGene: geneName,
      cancerTypeLabel,
      initialMolecularTypes: [...defaultMolecularTypes],
      selectedFilterGene: geneName,
      geneOptions: [],
      geneOptionsLoading: false,
      molecularTypeOptions: [],
      selectedMolecularTypes: [...defaultMolecularTypes],
      searchTerm: '',
      significantOnly: false,
      genePanel: null,
      loader: false,
      tableData: [],
      totalRows: 0,
    };
  }

  componentDidMount() {
    this.fetchGeneOptions();
    this.fetchGeneFilterOptions();
    this.fetchGeneData();
  }

  fetchGeneOptions = () => {
    this.setState({ geneOptionsLoading: true });

    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/biomarker-unique-gene`)
      .then((res) => {
        const geneOptions = toBiomarkerGeneOptions(res?.data?.data?.attributes?.unique_genes);

        this.setState({
          geneOptions: geneOptions.length > 0 ? geneOptions : GENE_OPTIONS,
        });
      })
      .catch((err) => {
        console.log(err);
        this.setState({ geneOptions: GENE_OPTIONS });
      })
      .finally(() => {
        this.setState({ geneOptionsLoading: false });
      });
  };

  fetchGeneFilterOptions = () => {
    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/gene-dropdown/projection-dropdowns`)
      .then((res) => {
        const prognosis = res.data?.dropdowns?.prognosis || {};
        const molecularTypeOptions = (prognosis.data_level || []).map((value) => String(value).trim());

        this.setState((prevState) => {
          const initialValues = (prevState.initialMolecularTypes || []).map((value) => String(value).trim());
          const validSelected = initialValues.filter((value) => molecularTypeOptions.includes(value));
          const nextSelected = validSelected.length > 0 ? validSelected : (molecularTypeOptions.length > 0 ? [molecularTypeOptions[0]] : []);

          return {
            molecularTypeOptions,
            selectedMolecularTypes: nextSelected,
          };
        });
      })
      .catch((err) => {
        console.log(err);
        this.setState({ molecularTypeOptions: [] });
      });
  };

  fetchGeneData = () => {
    const { selectedGene, selectedMolecularTypes, currentView } = this.state;
    const geneName = selectedGene || 'ACVR1B';
    const selectedTypes = (selectedMolecularTypes.length > 0 ? selectedMolecularTypes : this.state.molecularTypeOptions)
      .map((value) => String(value).trim())
      .filter(Boolean);

    if (!geneName || selectedTypes.length === 0) {
      this.setState({ tableData: [], totalRows: 0, loader: false });
      return;
    }

    const filterType = FILTER_TAB_MAP[currentView] || 'cohorts';

    this.setState({ loader: true, genePanel: null, tableData: [], totalRows: 0 });

    axiosInstance
      .post(`${process.env.REACT_APP_BASE_URL}api/treatment-ranking/filter`, {
        gene_name: geneName,
        molecular_type: selectedTypes,
        filter_type: filterType,
        ...(this.state.significantOnly ? { sig: true } : {}),
      })
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? [];
        const data = Array.isArray(payload) ? payload : (payload.data || []);
        this.setState({
          tableData: data,
          totalRows: res?.data?.total ?? data.length,
        });
      })
      .catch((err) => {
        console.log(err);
        this.setState({ tableData: [], totalRows: 0 });
      })
      .finally(() => {
        this.setState({ loader: false });
      });
  };

  getDisplayedRows = () => {
    const { tableData, searchTerm } = this.state;
    const term = searchTerm.trim().toLowerCase();

    return (tableData || []).filter((row) => {
      const searchMatches = !term || Object.values(row).join(' ').toLowerCase().includes(term);
      return searchMatches;
    });
  };

  handleFilterGeneChange = (selected) => {
    const nextGene = selected?.value || 'ACVR1B';
    this.setState({ selectedFilterGene: nextGene });
  };

  handleMolecularToggle = (e) => {
    const { value, checked } = e.target;
    const selectedValue = String(value).trim();

    this.setState((prev) => {
      const selected = prev.selectedMolecularTypes.includes(selectedValue)
        ? prev.selectedMolecularTypes.filter((item) => item !== selectedValue)
        : [...prev.selectedMolecularTypes, selectedValue];

      return {
        selectedMolecularTypes: checked ? selected : prev.selectedMolecularTypes.filter((item) => item !== selectedValue),
      };
    });
  };

  handleTabChange = (view) => {
    this.setState({ currentView: view, genePanel: null, searchTerm: '', significantOnly: false }, this.fetchGeneData);
  };

  handleSearchChange = (event) => {
    this.setState({ searchTerm: event.target.value });
  };

  handleSignificantOnlyChange = (event) => {
    this.setState({ significantOnly: event.target.checked }, this.fetchGeneData);
  };

  onReset = () => {
    const initialGene = this.state.initialGene || 'ACVR1B';
    const initialMolecularTypes = [...(this.state.initialMolecularTypes || [])]
      .map((value) => String(value).trim());
    const searchParams = new URLSearchParams(this.props.location?.search || '');
    searchParams.set('geneName', initialGene);
    searchParams.set('expressionType', initialMolecularTypes.join(','));
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: `?${searchParams.toString()}`,
    });
    this.setState({
      currentView: 'cohorts',
      selectedGene: initialGene,
      selectedFilterGene: initialGene,
      selectedMolecularTypes: initialMolecularTypes,
      searchTerm: '',
      significantOnly: false,
      genePanel: null,
    }, this.fetchGeneData);
  };

  onApply = () => {
    const selectedGene = this.state.selectedFilterGene;
    const searchParams = new URLSearchParams(this.props.location?.search || '');
    searchParams.set('geneName', selectedGene);
    searchParams.set('expressionType', this.state.selectedMolecularTypes.join(','));
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: `?${searchParams.toString()}`,
    });
    this.setState({ selectedGene, initialGene: selectedGene, genePanel: null }, this.fetchGeneData);
  };

  getRowKey = (row) => `${row.id ?? ''}|${row.gene_name}|${row.cohort || row.data_source || row.treatment || 'row'}|${row.data_level || row.treatment || 'level'}|${row.histological_subtype || ''}`;

  handleGeneClick = (row) => {
    const { currentView } = this.state;
    const geneName = row.gene_name;
    const plots = [{
      id: `${geneName}-${currentView}-${row.data_level || row.treatment}`,
      histological_subtype: row.histological_subtype,
      dataLevel: row.data_level || 'fusion',
      category: row.category || 'survival protective',
      cancer_type: row.cancer_type,
      estimate: row.estimate,
      p_value: row.p_value || 0.05,
      // include Cox type for cohort/prognosis plots so the detail API receives it
      coxType: row.cox_type || row.coxType,
    }];

    this.setState({
      genePanel: {
        geneName,
        row,
        plots,
        rowKey: this.getRowKey(row),
        context: currentView === 'treatment' ? { treatment: row.treatment, SurgRad: row.surg_rad || 'AllSamples', analysisType: 'Partial' } : {},
      },
    });
  };

  closeGenePanel = () => this.setState({ genePanel: null });

  exportCsv = () => {
    const rows = this.getDisplayedRows();
    if (!rows.length) return;
    const columns = Object.keys(rows[0]);
    const csv = [columns.join(','), ...rows.map((row) => columns.map((key) => `"${String(row[key] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.state.selectedGene || 'gene'}_${this.state.currentView}_results.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  getColumns = () => {
    const { currentView } = this.state;

    if (currentView === 'cohorts') {
      return [
        { name: 'Gene Name', selector: row => row.gene_name, sortable: true, width: GENE_COL_WIDTH, cell: row => <div className="bmt-gene-link">{row.gene_name}</div> },
        { name: 'Data Source', selector: row => row.data_source, sortable: true },
        { name: 'p-value', selector: row => Number(row.p_value), sortable: true, cell: row => formatStat(row.p_value, 4) },
        { name: 'Data Level', selector: row => row.data_level, sortable: true },
        { name: 'Cancer Type', selector: row => row.cancer_type, sortable: true },
        { name: 'Histological Subtype', selector: row => row.histological_subtype, sortable: true },
        { name: 'Category', selector: row => row.category, sortable: true },
        { name: 'Hazard Ratio', selector: row => Number(row.hr), sortable: true, cell: row => formatStat(row.hr, 4) },
        { name: 'log(HR)', selector: row => Number(row.log_hr), sortable: true, cell: row => formatStat(row.log_hr, 4) },
        { name: 'Standard Error', selector: row => Number(row.se), sortable: true, cell: row => formatStat(row.se, 4) },
        { name: 'z', selector: row => Number(row.z), sortable: true, cell: row => formatStat(row.z, 4) },
        { name: 'CI Lower', selector: row => Number(row.ci_lower), sortable: true, cell: row => formatStat(row.ci_lower, 4) },
        { name: 'CI Upper', selector: row => Number(row.ci_upper), sortable: true, cell: row => formatStat(row.ci_upper, 4) },
        { name: 'Cox Type', selector: row => row.cox_type, sortable: true },
      ];
    }

    if (currentView === 'treatment') {
      return [
        { name: 'Treatment', selector: row => row.treatment, sortable: true, width: '220px' },
        { name: 'Gene Name', selector: row => row.gene_name, sortable: true, width: GENE_COL_WIDTH, cell: row => <div className="bmt-gene-link">{row.gene_name}</div> },
        { name: 'Estimate', selector: row => Number(row.estimate), sortable: true, cell: row => formatStat(row.estimate, 4) },
        { name: 'Standard Error', selector: row => Number(row.std_error), sortable: true, cell: row => formatStat(row.std_error, 4) },
        { name: 'Statistic', selector: row => Number(row.statistic), sortable: true, cell: row => formatStat(row.statistic, 4) },
        { name: 'p-value', selector: row => Number(row.p_value), sortable: true, cell: row => formatStat(row.p_value, 4) },
        { name: 'Data Level', selector: row => row.data_level, sortable: true },
        { name: 'Cancer Type', selector: row => row.cancer_type, sortable: true },
        { name: 'Histological Subtype', selector: row => row.histological_subtype, sortable: true },
        { name: 'Surgery / Radiation', selector: row => row.surg_rad || 'AllSamples', sortable: true },
        { name: 'Category', selector: row => row.category, sortable: true },
        { name: 'Cohort', selector: row => row.cohort, sortable: true },
        { name: 'SEER Version', selector: row => row.see_rversion || row.seer_version || '', sortable: true },
      ];
    }

    return [
      { name: 'Gene Name', selector: row => row.gene_name, sortable: true, width: GENE_COL_WIDTH, cell: row => <div className="bmt-gene-link">{row.gene_name}</div> },
      { name: 'Cohort', selector: row => row.cohort, sortable: true },
      { name: 'p-value', selector: row => Number(row.p_value), sortable: true, cell: row => formatStat(row.p_value, 4) },
      { name: 'Data Level', selector: row => row.data_level, sortable: true },
      { name: 'Cancer Type', selector: row => row.cancer_type, sortable: true },
      { name: 'Histological Subtype', selector: row => row.histological_subtype, sortable: true },
      { name: 'Category', selector: row => row.category, sortable: true },
      { name: 'Estimate', selector: row => Number(row.estimate), sortable: true, cell: row => formatStat(row.estimate, 4) },
      { name: 'Standard Error', selector: row => Number(row.std_error), sortable: true, cell: row => formatStat(row.std_error, 4) },
      { name: 'Statistic', selector: row => Number(row.statistic), sortable: true, cell: row => formatStat(row.statistic, 4) },
      { name: 'SEER Version', selector: row => row.see_rversion || row.seer_version || '', sortable: true },
    ];
  };

  render() {
    const { currentView, selectedGene, selectedFilterGene, selectedMolecularTypes, genePanel, searchTerm, significantOnly, loader } = this.state;
    const rows = this.getDisplayedRows();
    const tableMode = currentView === 'cohorts' ? 'cohort' : 'population';
    const groups = currentView === 'treatment' ? [] : getPrognosisTableGroups(this.state.tableData, tableMode);

    return (
      <>
        <div className="bmt-page">
          <Link className="bmt-back-link" to={{ pathname: '/app/bioMarker' }}>
            <img src={backlogo} alt="" />
            <span>Back</span>
          </Link>

          <div className="bmt-layout">
            <div className="bmt-filter-col">
              <div className="bmt-filter-card">
                <div className="bmt-flow-badge">Biomarker Browser · By Gene</div>
                <div className="bmt-filter-title">Filters</div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Cancer type</span>
                  <div className="bmt-static-field">{this.state.cancerTypeLabel}</div>
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Gene</span>
                  <BiomarkerGeneSelect
                    options={this.state.geneOptions.length > 0 ? this.state.geneOptions : GENE_OPTIONS}
                    value={selectedFilterGene ? { value: selectedFilterGene, label: selectedFilterGene } : null}
                    onChange={this.handleFilterGeneChange}
                    placeholder={this.state.geneOptionsLoading ? 'Loading genes...' : 'Select gene...'}
                    isLoading={this.state.geneOptionsLoading}
                    isClearable
                    styles={roundedSelectStyles}
                  />
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Molecular type</span>
                  <div className="bmt-checkbox-grid">
                    {this.state.molecularTypeOptions.length > 0 ? (
                      this.state.molecularTypeOptions.map((type) => (
                        <label key={type} className="bmt-checkbox-label">
                          <input
                            type="checkbox"
                            value={type}
                            checked={selectedMolecularTypes.includes(String(type).trim())}
                            onChange={this.handleMolecularToggle}
                          />
                          {type}
                        </label>
                      ))
                    ) : (
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>Loading molecular types...</div>
                    )}
                  </div>
                </div>

                <div className="bmt-filter-actions">
                  <button type="button" className="bmt-btn-reset" onClick={this.onReset}>
                    <FiRotateCcw size={14} />
                    Reset
                  </button>
                  <button type="button" className="bmt-btn-apply" onClick={this.onApply}>
                    Apply filters
                  </button>
                </div>
              </div>
            </div>

            <div className="bmt-results-col">
              <div className="bmt-results-card prognosis-results-card">
                <div className="bmt-results-header">
                  <div>
                    <h2 className="bmt-results-title">Results for {selectedGene}</h2>
                    <div className="bmt-results-subtitle">Biomarker associations</div>
                  </div>
                  <button type="button" className="bmt-btn-download" onClick={this.exportCsv} disabled={rows.length === 0}>
                    <AiOutlineDownload size={16} />
                    Download CSV
                  </button>
                </div>

                <div className="bmt-chip-row">
                  <span className="bmt-chip">Non-Small Cell Lung Cancer</span>
                  <span className="bmt-chip">{selectedGene}</span>
                </div>

                <div className="bmt-toolbar" style={{ marginBottom: '12px' }}>
                  <div className="tp-search-wrap">
                    <AiOutlineSearch className="tp-search-icon" size={14} />
                    <input type="text" placeholder="Search results" aria-label="Search gene results" value={searchTerm} onChange={this.handleSearchChange} />
                  </div>
                  <label className="bmt-checkbox-label">
                    <input type="checkbox" checked={significantOnly} onChange={this.handleSignificantOnlyChange} />
                    Significant only
                  </label>
                  <span className="tp-badge" role="status">{rows.length} result{rows.length === 1 ? '' : 's'}</span>
                  {groups.length > 0 && <div className="prognosis-legend" aria-label="Column groups">
                    {groups.map(group => <span key={group.id} className={`metric-${group.id}`}><i aria-hidden="true" />{group.label.replace(' Cox model', '')}</span>)}
                  </div>}
                </div>

                <div className="bmt-tab-row" style={{ display: 'flex', gap: '18px', borderBottom: '1px solid #eef0f4', marginBottom: '18px' }}>
                  {['cohorts', 'prognotic', 'treatment'].map((view) => (
                    <button
                      key={view}
                      type="button"
                      onClick={() => this.handleTabChange(view)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: currentView === view ? '#545cd8' : '#666d7a',
                        fontWeight: currentView === view ? 700 : 600,
                        fontSize: '0.82rem',
                        padding: '0 0 10px',
                        borderBottom: currentView === view ? '2px solid #545cd8' : '2px solid transparent',
                        cursor: 'pointer',
                      }}
                    >
                      {view === 'cohorts' ? 'Cohort results' : view === 'prognotic' ? 'Population projection' : 'Treatment projection'}
                    </button>
                  ))}
                </div>

                {loader ? (
                  <div className="backdrop">
                    <div className="loader">
                      <Loader type="ball-pulse-rise" />
                    </div>
                  </div>
                ) : currentView !== 'treatment' ? (
                  <PrognosisResultsTable
                    key={currentView}
                    rows={rows}
                    groups={groups}
                    mode={tableMode}
                    categoryLabel={currentView === 'prognotic' ? 'Concordant category' : 'Category'}
                    selectedRowKey={genePanel?.rowKey}
                    getRowKey={this.getRowKey}
                    onGeneClick={this.handleGeneClick}
                  />
                ) : (
                  <div className="bmt-table-wrap tp-data-table prognosis-table-shell gene-treatment-table">
                    <DataTable
                      columns={this.getColumns()}
                      data={rows}
                      onRowClicked={this.handleGeneClick}
                      customStyles={dataTableCustomStyles}
                      noDataComponent={<div style={{ padding: '24px', fontSize: '0.85rem', color: '#888' }}>No genes match the current search/filter.</div>}
                      highlightOnHover
                      fixedHeader
                      fixedHeaderScrollHeight="58vh"
                      sortIcon={<FiChevronDown size={12} />}
                      conditionalRowStyles={[{
                        when: row => !!genePanel && this.getRowKey(row) === genePanel.rowKey,
                        style: { backgroundColor: '#eef0ff' },
                      }]}
                      pagination
                      paginationPerPage={PAGE_SIZE}
                      paginationComponentOptions={{ noRowsPerPage: true }}
                      paginationComponent={RoundedPagination}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {genePanel && currentView === 'cohorts' && (
          <PrognosisGeneDetailPanel key={genePanel.rowKey} geneName={genePanel.geneName} row={genePanel.row} mode="cohort" onClose={this.closeGenePanel} />
        )}
        {genePanel && currentView !== 'cohorts' && (
          <GeneDetailPanel
            geneName={genePanel.geneName}
            plots={genePanel.plots}
            onClose={this.closeGenePanel}
            lambdaType={currentView === 'treatment' ? 'treatment' : currentView === 'cohorts' ? 'cohorts' : 'prognosis'}
            context={genePanel.context || {}}
          />
        )}
      </>
    );
  }
}

export default BioMarkerGeneResultPage;
